/**
 * Supercapacitor Power Chart Component
 * Visualizes P_SC(t) = P(t) - MA(P(t)) 
 * Based on IECON2023 Paper Figure 8
 * Shows the power handled by the supercapacitor (burst components)
 */

import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { Box, Typography, Card, CardContent, Alert, LinearProgress, useTheme } from '@mui/material';

interface SupercapPowerChartProps {
  data: {
    timestamp: string[];
    power: number[];
    power_ma: number[];
    P_SC: number[];
  };
  title?: string;
  height?: number;
}

const SupercapPowerChart: React.FC<SupercapPowerChartProps> = ({
  data,
  title = 'Supercapacitor Power P_SC(t)',
  height = 400
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [progressiveIndex, setProgressiveIndex] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const animationRef = useRef<number | null>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Dynamic colors based on theme - use useMemo to avoid recreating on every render
  const colors = useMemo(() => ({
    background: isDark ? '#0d1117' : '#ffffff',
    text: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
    grid: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    discharge: isDark ? '#ef5350' : '#dc2626',
    dischargeLight: isDark ? 'rgba(239, 83, 80, 0.2)' : 'rgba(220, 38, 38, 0.2)',
    charge: isDark ? '#66bb6a' : '#16a34a',
    chargeLight: isDark ? 'rgba(102, 187, 106, 0.2)' : 'rgba(22, 163, 74, 0.2)'
  }), [isDark]);

  const drawChart = useCallback((maxDataPoints?: number) => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.P_SC || data.P_SC.length === 0) {
      console.log('⚠️ SupercapPowerChart: No data to display', {
        hasCanvas: !!canvas,
        hasData: !!data,
        hasPSC: !!data?.P_SC,
        pscLength: data?.P_SC?.length,
        dataKeys: data ? Object.keys(data) : null
      });
      return;
    }

    console.log('🔋 Drawing P_SC chart with', data.P_SC.length, 'datapoints');

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 🔥 PERFORMANCE FIX: Reduce data to max 5000 points
    const MAX_POINTS = 3000;
    let P_SC_data = data.P_SC;
    
    if (P_SC_data.length > MAX_POINTS) {
      console.log(`⚡ Downsampling from ${P_SC_data.length} to ${MAX_POINTS} points`);
      const step = Math.ceil(P_SC_data.length / MAX_POINTS);
      P_SC_data = P_SC_data.filter((_: number, index: number) => index % step === 0);
      console.log(`✅ Downsampled to ${P_SC_data.length} points (step: ${step})`);
    }
    
    // Progressive rendering: only use first N datapoints
    const dataLimit = maxDataPoints || P_SC_data.length;
    const limitedData = P_SC_data.slice(0, dataLimit);
    const labels = Array.from({ length: limitedData.length }, (_, i) => i);

    console.log('🟢 Drawing chart with', limitedData.length, '/', P_SC_data.length, 'datapoints');

    // Chart dimensions
    const padding = 80;
    const paddingBottom = 100; // Extra space for legend at bottom
    const chartWidth = canvas.width - padding * 2;
    const chartHeight = canvas.height - padding - paddingBottom;

    // Data ranges
    const maxValue = Math.max(...limitedData);
    const minValue = Math.min(...limitedData);
    const maxTime = labels.length - 1;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = colors.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Helper functions
    const xScale = (time: number) => padding + (time / maxTime) * chartWidth;
    const yScale = (value: number) => padding + chartHeight - ((value - minValue) / (maxValue - minValue)) * chartHeight;

    // Draw grid
    ctx.strokeStyle = colors.grid;
    ctx.lineWidth = 1;

    // Horizontal grid lines
    for (let i = 0; i <= 10; i++) {
      const y = padding + (i / 10) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(padding + chartWidth, y);
      ctx.stroke();
    }

    // Vertical grid lines
    for (let i = 0; i <= 10; i++) {
      const x = padding + (i / 10) * chartWidth;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, padding + chartHeight);
      ctx.stroke();
    }

    // Draw axes
    ctx.strokeStyle = colors.text;
    ctx.lineWidth = 2;

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, padding + chartHeight);
    ctx.stroke();

    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding + chartHeight);
    ctx.lineTo(padding + chartWidth, padding + chartHeight);
    ctx.stroke();

    // Draw axis labels
    ctx.fillStyle = colors.text;
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';

    // X-axis label
    ctx.fillText('Time t [samples]', canvas.width / 2, padding + chartHeight + 40);

    // Y-axis label (rotated)
    ctx.save();
    ctx.translate(20, canvas.height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Supercap Power P_SC [W]', 0, 0);
    ctx.restore();

    // Draw Y-axis ticks and labels
    ctx.textAlign = 'right';
    ctx.font = '12px Arial';
    for (let i = 0; i <= 5; i++) {
      const value = minValue + (i / 5) * (maxValue - minValue);
      const y = yScale(value);

      // Tick mark
      ctx.beginPath();
      ctx.moveTo(padding - 5, y);
      ctx.lineTo(padding + 5, y);
      ctx.stroke();

      // Label
      ctx.fillText(Math.round(value).toString(), padding - 10, y + 5);
    }

    // Draw X-axis ticks and labels
    ctx.textAlign = 'center';
    for (let i = 0; i <= 5; i++) {
      const time = (i / 5) * maxTime;
      const x = xScale(time);

      // Tick mark
      ctx.beginPath();
      ctx.moveTo(x, padding + chartHeight - 5);
      ctx.lineTo(x, padding + chartHeight + 5);
      ctx.stroke();

      // Label
      ctx.fillText(Math.round(time).toString(), x, padding + chartHeight + 20);
    }

    // Draw zero line (important for P_SC visualization)
    ctx.strokeStyle = colors.text;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    const zeroY = yScale(0);
    ctx.moveTo(padding, zeroY);
    ctx.lineTo(padding + chartWidth, zeroY);
    ctx.stroke();
    ctx.setLineDash([]);

    // 🔥 PERFORMANCE FIX: Use Path2D for better performance
    // Fill positive area (Supercap discharge - burst)
    ctx.fillStyle = colors.dischargeLight;
    const positivePath = new Path2D();
    positivePath.moveTo(xScale(0), zeroY);
    for (let i = 0; i < limitedData.length; i++) {
      const x = xScale(i);
      const y = yScale(Math.max(0, limitedData[i]));
      positivePath.lineTo(x, y);
    }
    positivePath.lineTo(xScale(limitedData.length - 1), zeroY);
    positivePath.closePath();
    ctx.fill(positivePath);

    // Fill negative area (Supercap charge - low load)
    ctx.fillStyle = colors.chargeLight;
    const negativePath = new Path2D();
    negativePath.moveTo(xScale(0), zeroY);
    for (let i = 0; i < limitedData.length; i++) {
      const x = xScale(i);
      const y = yScale(Math.min(0, limitedData[i]));
      negativePath.lineTo(x, y);
    }
    negativePath.lineTo(xScale(limitedData.length - 1), zeroY);
    negativePath.closePath();
    ctx.fill(negativePath);

    // Draw P_SC line (colored by positive/negative)
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    
    for (let i = 0; i < limitedData.length; i++) {
      const x = xScale(i);
      const y = yScale(limitedData[i]);
      const value = limitedData[i];
      
      if (i === 0) {
        ctx.strokeStyle = value >= 0 ? colors.discharge : colors.charge;
        ctx.beginPath();
        ctx.moveTo(x, y);
      } else {
        const prevValue = limitedData[i - 1];
        // If sign changes, end current path and start new one
        if ((value >= 0 && prevValue < 0) || (value < 0 && prevValue >= 0)) {
          ctx.stroke();
          ctx.strokeStyle = value >= 0 ? colors.discharge : colors.charge;
          ctx.beginPath();
          ctx.moveTo(xScale(i - 1), yScale(prevValue));
          ctx.lineTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
    }
    ctx.stroke();

    // Draw legend - horizontal at bottom
    const legendY = padding + chartHeight + 65;
    const legendItemWidth = 280;

    ctx.font = '12px Arial';
    ctx.textAlign = 'left';

    let legendItemX = padding;

    // Positive (Discharge)
    ctx.fillStyle = isDark ? 'rgba(239, 83, 80, 0.3)' : 'rgba(220, 38, 38, 0.3)';
    ctx.fillRect(legendItemX, legendY - 5, 20, 10);
    ctx.strokeStyle = colors.discharge;
    ctx.lineWidth = 2;
    ctx.strokeRect(legendItemX, legendY - 5, 20, 10);
    ctx.fillStyle = colors.text;
    ctx.fillText('P_SC > 0: Supercap Discharge (Burst)', legendItemX + 25, legendY + 4);

    legendItemX += legendItemWidth;

    // Negative (Charge)
    ctx.fillStyle = isDark ? 'rgba(102, 187, 106, 0.3)' : 'rgba(22, 163, 74, 0.3)';
    ctx.fillRect(legendItemX, legendY - 5, 20, 10);
    ctx.strokeStyle = colors.charge;
    ctx.lineWidth = 2;
    ctx.strokeRect(legendItemX, legendY - 5, 20, 10);
    ctx.fillStyle = colors.text;
    ctx.fillText('P_SC < 0: Supercap Charge (Low Load)', legendItemX + 25, legendY + 4);

    legendItemX += legendItemWidth;

    // Zero line
    ctx.strokeStyle = colors.text;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(legendItemX, legendY);
    ctx.lineTo(legendItemX + 20, legendY);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = colors.text;
    ctx.fillText('Battery Baseline', legendItemX + 25, legendY + 4);

  }, [data, colors, isDark]);

  // Progressive rendering animation effect
  useEffect(() => {
    if (!data?.P_SC) return;

    // Apply downsampling first
    const MAX_POINTS = 3000;
    let P_SC_data = data.P_SC;
    
    if (P_SC_data.length > MAX_POINTS) {
      const step = Math.ceil(P_SC_data.length / MAX_POINTS);
      P_SC_data = P_SC_data.filter((_: number, index: number) => index % step === 0);
    }

    const totalPoints = P_SC_data.length;

    // Only animate if we have more than 500 points
    if (totalPoints <= 500) {
      drawChart(totalPoints);
      return;
    }

    // Start animation
    setIsAnimating(true);
    setProgressiveIndex(0);

    // Animation parameters
    const startPoints = Math.min(100, totalPoints); // Start with 100 points
    const pointsPerFrame = Math.ceil((totalPoints - startPoints) / 60); // Complete in ~60 frames
    let currentPoints = startPoints;

    const animate = () => {
      if (currentPoints >= totalPoints) {
        setIsAnimating(false);
        drawChart(totalPoints);
        return;
      }

      currentPoints = Math.min(currentPoints + pointsPerFrame, totalPoints);
      setProgressiveIndex(currentPoints);
      drawChart(currentPoints);

      animationRef.current = requestAnimationFrame(animate);
    };

    // Start with initial points
    drawChart(startPoints);
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [data, drawChart]);

  useEffect(() => {
    drawChart();
  }, [drawChart]);

  if (!data || !data.P_SC || data.P_SC.length === 0) {
    return (
      <Alert severity="info">
        Keine Supercap-Leistungsdaten verfügbar. Bitte analysieren Sie zuerst das Lastprofil.
      </Alert>
    );
  }

  const totalDataPoints = data?.P_SC?.length || 0;
  const loadingProgress = totalDataPoints > 0 ? (progressiveIndex / totalDataPoints) * 100 : 0;

  return (
    <Box>
      {title && (
        <Typography variant="h6" fontWeight="600" gutterBottom>
          {title}
        </Typography>
      )}


      {/* Progressive loading indicator */}
      {isAnimating && totalDataPoints > 500 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant='caption' color='text.secondary'>
              Lade Datenpunkte...
            </Typography>
            <Typography variant='caption' color='primary'>
              {progressiveIndex.toLocaleString()} / {totalDataPoints.toLocaleString()} Punkte ({loadingProgress.toFixed(0)}%)
            </Typography>
          </Box>
          <LinearProgress
            variant='determinate'
            value={loadingProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(220, 38, 38, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: 'linear-gradient(90deg, #dc2626 0%, #f97316 100%)'
              }
            }}
          />
        </Box>
      )}

      <canvas
        ref={canvasRef}
        width={1200}
        height={height}
        style={{ width: '100%', height: 'auto', maxWidth: '100%' }}
      />
    </Box>
  );
};

export default SupercapPowerChart;
